import { Component, OnInit, Inject } from '@angular/core'
import { Router, NavigationStart, NavigationEnd, ActivatedRoute } from '@angular/router'
import { Location, PopStateEvent, DOCUMENT } from '@angular/common'

import { environment } from 'environments/environment'

import { User } from './models'
import { UserService } from './services'
import { filter, map } from 'rxjs/operators'
import { Title } from '@angular/platform-browser'

@Component({
  standalone: false,
  selector: 'app-root',
  template: '<router-outlet></router-outlet>'
})
export class AppComponent implements OnInit {
  private lastPoppedUrl: string
  private yScrollStack: number[] = []
  private user: User

  constructor(
    private router: Router,
    private location: Location,
    private userService: UserService,
    private titleService: Title,
    @Inject(DOCUMENT) private doc: any
  ) { }

  public ngOnInit(): void {
    this.initUser()
    this.loadAnalytics()
    this.initAutoScroll()
    this.addCss()
    this.registerRouterTitleChanges()
  }

  // THIS IS REQUIRED FOR PREVIEW MODE TO FUNCTION AS DESIRED
  public initUser(): void {
    this.userService.current.subscribe((user: User) => {
      if (!user || !user.isAuthenticated()) return
      this.user = user
    })
    this.userService.findCurrent()
  }

  private initAutoScroll(): void {
    // auto scroll to top on route change
    this.location.subscribe((ev: PopStateEvent) => {
      this.lastPoppedUrl = ev.url
    })
    this.router.events.subscribe((ev: any) => {
      if (ev instanceof NavigationStart) {
        if (ev.url !== this.lastPoppedUrl) {
          this.yScrollStack.push(window.scrollY)
        }
      } else if (ev instanceof NavigationEnd) {
        if (ev.url === this.lastPoppedUrl) {
          this.lastPoppedUrl = undefined
          window.scrollTo(0, this.yScrollStack.pop())
        } else {
          window.scrollTo(0, 0)
        }
      }
    })
  }

  private loadAnalytics(): void {
    this.addLocalGTM()
    this.addGlobalGTM()
  }

  private addCss(): void {
    const style = this.doc.createElement('link')
    style.setAttribute('rel', 'stylesheet')
    style.setAttribute('type', 'text/css')
    style.setAttribute('href', environment.cssUrl)
    this.doc.getElementsByTagName('head')[0].appendChild(style)
  }

  private addLocalGTM(): void {
    if (!environment.localGTMCode) return
    const script = this.doc.createElement('script')
    script.type = 'text/javascript'
    script.innerHTML = `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({`
      + `'gtm.start':new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],`
      + `j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=`
      + `'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);})`
      + `(window,document,'script','dataLayer','${environment.localGTMCode}');`
    this.doc.getElementsByTagName('head')[0].appendChild(script)
  }

  private addGlobalGTM(): void {
    if (!environment.globalGTMCode) return
    const script = this.doc.createElement('script')
    script.type = 'text/javascript'
    script.innerHTML = `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({`
      + `'gtm.start':new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],`
      + `j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=`
      + `'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);})`
      + `(window,document,'script','dataLayer','${environment.globalGTMCode}');`
    this.doc.getElementsByTagName('head')[0].appendChild(script)
  }

  private registerRouterTitleChanges() {
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        map(() => {
          let route: ActivatedRoute = this.router.routerState.root
          let routeTitle = ''
          while (route.firstChild) {
            route = route.firstChild
          }
          if (route.snapshot.data['title']) {
            routeTitle = route.snapshot.data['title']
          }
          return routeTitle
        })
      )
      .subscribe((title: string) => {
        if (title) {
          this.titleService.setTitle(title || 'SportsEngine Memberships')
        }
      })
  }
}
